import { Component, OnInit,Input } from '@angular/core';
import { ChartType } from 'chart.js';
import {
  chartColors,
  RtoBarChartConstants,
  ToastTitle,
} from '@constants/global.enum';
import { DatePipe } from '@angular/common';
import { QuarterlyFeedbackComplianceService } from '@services/quarterly-feedback-compliance.service';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PEOPLE_METRICS } from '@constants/people-metrics.constants';
import { ToasterService } from '@services/toaster.service';
import { ErrorMessages } from '@constants/constants';
import { RTOComplianceResponse } from '@interfaces/quarterly-feedback.model';
import { yearDateFormat } from '@constants/dateFormat';
import { AuthService } from '@app/core/auth.service';

@Component({
  selector: 'app-rto-bar-chart',
  templateUrl: './rto-bar-chart.component.html',
  styleUrls: ['./rto-bar-chart.component.scss'],
})
export class RtoBarChartComponent implements OnInit {
  @Input() vendorId: string | null = null;
  private readonly destroy$ = new Subject<void>();
  barChartLabels: string[] = [];
  barChartData: any[] = [];
  barChartOptions: any = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: RtoBarChartConstants.legendPosition,
        labels: {
          usePointStyle: true,
          font: {
            size: RtoBarChartConstants.legendFontSize,
          },
          color: chartColors.textDark,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: chartColors.tooltipBackground,
        titleColor: chartColors.tooltipTitle,
        bodyColor: chartColors.tooltipBody,
        borderColor: chartColors.dividerGrey,
        borderWidth: RtoBarChartConstants.toolTipBorderWidth,
      },
      datalabels: {
        color: chartColors.backgroundWhite,
        anchor: RtoBarChartConstants.dataLabelAnchor,
        align: RtoBarChartConstants.dataLabelAlign,
        font: {
          size: RtoBarChartConstants.dataLabelFontSize,
        },
      },
    },
    hover: {
      mode: null,
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawTicks: true,
        },
        ticks: {
          display: true,
          align: RtoBarChartConstants.tickAlign,
          font: {
            size: RtoBarChartConstants.tickFontSize,
            color: chartColors.textDark,
          },
        },
        barThickness: RtoBarChartConstants.xBarThickness,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: false,
          color: chartColors.dividerGrey,
          drawTicks: true,
        },
        ticks: {
          display: true,
          align: RtoBarChartConstants.tickAlign,
          font: {
            size: RtoBarChartConstants.tickFontSize,
            color: chartColors.textDark,
          },
          stepSize: RtoBarChartConstants.yTickStepSize,
        },
      },
    },
  };
  chartType: ChartType = 'bar';

  constructor(
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private quarterlyFeedbackComplianceService: QuarterlyFeedbackComplianceService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadRtoComplianceData();
  }

  private getParamValue(params: any, keys: string[]): string {
    for (const key of keys) {
      if (params?.[key]) {
        return params[key];
      }
    }
    return '';
  }

  private loadRtoComplianceData(): void {
    if (!this.vendorId) {
      this.toasterService.showError(
        PEOPLE_METRICS.MESSAGES.MISSING_IDS_ERROR,
        ToastTitle.ERROR,
      );
      return;
    }
    const employeeId = this.authService.userProfile?.employeeId;
    this.quarterlyFeedbackComplianceService
      .getRtoCompliance(this.vendorId, employeeId?.toString() || '')
      .subscribe({
        next: (response: { data: RTOComplianceResponse }) => {
          this.generateYearlyData(response.data);
        },
        error: (error) => {
          this.showError(ErrorMessages.NoDataAvailable);
        },
      });
  }

  generateYearlyData(rtoData: RTOComplianceResponse): void {
    const now = new Date();
    const compliantData: number[] = new Array(12).fill(0);
    const nonCompliantData: number[] = new Array(12).fill(0);
    const labels: string[] = [];

    // Create the labels for the last 12 months
    const datePipe = new DatePipe('en-US');
    for (let i = 1; i <= 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = datePipe.transform(monthDate, yearDateFormat.MonthYear);
      if (monthLabel) {
        labels.unshift(monthLabel);
      }
    }

    // Aggregate data from the API response
    if (rtoData && Array.isArray(rtoData)) {
      rtoData?.forEach((item: any) => {
        // Create a Date object for the data point
        const dataDate = new Date(
          item.attendanceYear,
          item.attendanceMonth - 1,
        );

        // Find the index of this data's month in our 12-month array
        const monthLabel = datePipe.transform(dataDate, yearDateFormat.MonthYear);
        const monthIndex = labels.indexOf(monthLabel as string);

        if (monthIndex !== -1) {
          if (item.complianceStatus === RtoBarChartConstants.barChartLabelCompliant) {
            compliantData[monthIndex]++;
          } else if (item.complianceStatus === RtoBarChartConstants.barChartLabelNonCompliant) {
            nonCompliantData[monthIndex]++;
          }
        }
      });
    }

    this.barChartLabels = labels;
    this.barChartData = [
      {
        data: compliantData,
        label: RtoBarChartConstants.barChartLabelCompliant,
        backgroundColor: chartColors.mintGreen,
      },
      {
        data: nonCompliantData,
        label: RtoBarChartConstants.barChartLabelNonCompliant,
        backgroundColor: chartColors.darkOrange,
      },
    ];
  }

  private showError(message: string): void {
    this.toasterService.showError(message, ToastTitle.ERROR);
  }
}
